import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

// ── Tipos ──────────────────────────────────────────────────────────────────────
export type Theme = "light" | "dark";

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeState | undefined>(undefined);

// ── Persistencia ───────────────────────────────────────────────────────────────
// Guardamos a preferencia num cookie (em vez de localStorage) para sobreviver
// a sandboxes que bloqueiam storage. O cookie nao tem dados sensiveis.
const COOKIE_NAME = "clinlinkhd_theme";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 ano

function readStoredTheme(): Theme | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`),
  );
  const value = match?.[1];
  if (value === "light" || value === "dark") return value;
  return null;
}

function writeStoredTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=${theme}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

// ── Provider ───────────────────────────────────────────────────────────────────
export function ThemeProvider({ children }: { children: ReactNode }) {
  // Inicial: SEMPRE light excepto se o utilizador ja escolheu dark explicitamente.
  // Nao seguimos prefers-color-scheme — light e o preferencial.
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = readStoredTheme();
    return stored ?? "light";
  });

  // Aplicar a classe no <html>
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    // Sincronizar meta theme-color para chrome do browser
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="theme-color"]',
    );
    if (meta) {
      meta.content = theme === "dark" ? "#1A1D24" : "#E4E7EE";
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeStoredTheme(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "light" ? "dark" : "light";
      writeStoredTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ theme, setTheme, toggle }),
    [theme, setTheme, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────────
export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme deve ser usado dentro de <ThemeProvider>");
  return ctx;
}
